/**
 * Utilitaires centralisés pour la gestion des erreurs
 * Permet une approche standardisée du traitement, de l'enregistrement 
 * et de l'affichage des erreurs dans l'application
 */

// Types d'erreurs pour une catégorisation cohérente
export enum ErrorType {
  VALIDATION = 'VALIDATION',
  API = 'API',
  AUTHENTICATION = 'AUTHENTICATION',
  DATABASE = 'DATABASE',
  NETWORK = 'NETWORK',
  UNKNOWN = 'UNKNOWN'
}

// Structure standardisée pour les erreurs
export interface AppError {
  type: ErrorType;
  message: string;
  originalError?: any;
  context?: Record<string, any>;
  timestamp: number;
}

/**
 * Crée un objet d'erreur standard à utiliser dans toute l'application
 */
export function createError(
  type: ErrorType,
  message: string,
  originalError?: any,
  context?: Record<string, any>
): AppError {
  return {
    type,
    message,
    originalError,
    context,
    timestamp: Date.now()
  };
}

/**
 * Traite une erreur en la convertissant dans notre format standard
 * et en effectuant les actions appropriées (journalisation, etc.)
 */
export function handleError(error: any, context?: Record<string, any>): AppError {
  // Par défaut, on considère que c'est une erreur inconnue
  let appError: AppError;
  
  // Déterminer le type d'erreur en fonction de sa structure ou de ses propriétés
  if (error?.response) {
    // Erreur d'API avec une réponse
    appError = createError(
      ErrorType.API,
      `Erreur API: ${error.response.status} ${error.response.statusText || ''}`,
      error,
      { ...context, statusCode: error.response.status }
    );
  } else if (error?.request) {
    // Erreur réseau (pas de réponse reçue)
    appError = createError(
      ErrorType.NETWORK,
      "Erreur réseau: Impossible de communiquer avec le serveur",
      error,
      context
    );
  } else if (error?.name === 'ValidationError' || error?.message?.includes('validation')) {
    // Erreur de validation
    appError = createError(
      ErrorType.VALIDATION,
      `Erreur de validation: ${error.message || 'Données invalides'}`,
      error,
      context
    );
  } else if (error instanceof Error) {
    // Erreur standard JS
    appError = createError(
      ErrorType.UNKNOWN,
      `Erreur: ${error.message}`,
      error,
      context
    );
  } else {
    // Cas générique pour tout autre type d'erreur
    appError = createError(
      ErrorType.UNKNOWN,
      typeof error === 'string' ? error : 'Une erreur inattendue est survenue',
      error,
      context
    );
  }
  
  // Journaliser l'erreur (en production, on pourrait envoyer à un service comme Sentry)
  console.error('[ERROR]', appError.type, appError.message, {
    context: appError.context,
    timestamp: new Date(appError.timestamp).toISOString(),
    originalError: process.env.NODE_ENV === 'development' ? appError.originalError : undefined
  });
  
  return appError;
}

/**
 * Obtient un message d'erreur adapté à l'utilisateur final
 */
export function getUserFriendlyErrorMessage(error: AppError): string {
  // Des messages génériques par type d'erreur
  switch (error.type) {
    case ErrorType.VALIDATION:
      return error.message;
    case ErrorType.API:
      return "Erreur lors de la communication avec le serveur. Veuillez réessayer ultérieurement.";
    case ErrorType.AUTHENTICATION:
      return "Erreur d'authentification. Veuillez vous reconnecter et réessayer.";
    case ErrorType.DATABASE:
      return "Erreur de base de données. Veuillez réessayer ultérieurement.";
    case ErrorType.NETWORK:
      return "Erreur de connexion. Veuillez vérifier votre connexion internet et réessayer.";
    case ErrorType.UNKNOWN:
    default:
      return "Une erreur inattendue est survenue. Veuillez réessayer ultérieurement.";
  }
}
